import React, { useEffect, useRef, useState } from "react"

// ── Config ────────────────────────────────────────────────────
const STORAGE_KEY    = "rcs_cookie_consent"
const CONSENT_VERSION = 1          // súbelo para volver a pedir consentimiento
const PRIVACY_LINK   = "/privacy"
const SHOW_DELAY_MS  = 900

// Brand colors (mismos que Chatbot / Footer)
const RED      = "#CC0000"
const RED_DARK = "#A80000"
const INK      = "#1A1A1A"
const BG       = "#F6F6F4"
const WHITE    = "#FFFFFF"
const BORDER   = "rgba(0,0,0,0.09)"
const MUTED    = "rgba(0,0,0,0.55)"

const CATEGORIES = [
  {
    key: "necessary",
    title: "Strictly Necessary",
    desc: "Required for the site to work (security, forms, saving your preferences). Always on.",
    locked: true,
  },
  {
    key: "analytics",
    title: "Analytics",
    desc: "Help us understand how visitors use the site so we can improve it.",
  },
  {
    key: "marketing",
    title: "Marketing",
    desc: "Used to show relevant ads and measure campaign performance.",
  },
]

// ── Storage helpers ───────────────────────────────────────────
function readConsent() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const data = JSON.parse(raw)
    return data?.version === CONSENT_VERSION ? data : null
  } catch {
    return null
  }
}

function saveConsent(prefs) {
  const data = {
    version: CONSENT_VERSION,
    necessary: true,
    analytics: !!prefs.analytics,
    marketing: !!prefs.marketing,
    timestamp: new Date().toISOString(),
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  } catch {}
  window.rcsCookieConsent = data
  // Otros scripts (GA, Meta Pixel, etc.) pueden escuchar este evento
  window.dispatchEvent(new CustomEvent("rcs:cookie-consent", { detail: data }))
  return data
}

// ── Icons ─────────────────────────────────────────────────────
function CookieIcon({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M21 12.5A9 9 0 1 1 11.5 3a3 3 0 0 0 3.5 3.5 3 3 0 0 0 3.5 3.5 3 3 0 0 0 2.5 2.5Z"
        stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <circle cx="8.5" cy="10" r="1.1" fill="currentColor" />
      <circle cx="12" cy="15.5" r="1.1" fill="currentColor" />
      <circle cx="16" cy="13" r="0.9" fill="currentColor" />
      <circle cx="8" cy="15" r="0.8" fill="currentColor" />
    </svg>
  )
}

function CloseIcon({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 6L18 18M18 6L6 18" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
    </svg>
  )
}

// ── Toggle ────────────────────────────────────────────────────
function Toggle({ checked, disabled, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className="relative shrink-0 rounded-full transition-colors duration-200"
      style={{
        width: 42,
        height: 24,
        background: checked ? (disabled ? "rgba(204,0,0,0.45)" : RED) : "rgba(0,0,0,0.18)",
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      <span
        className="absolute top-[3px] rounded-full transition-all duration-200"
        style={{
          width: 18,
          height: 18,
          left: checked ? 21 : 3,
          background: WHITE,
          boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
        }}
      />
    </button>
  )
}

// ── Component ─────────────────────────────────────────────────
export default function CookieConsent() {
  const [open, setOpen]               = useState(false)
  const [visible, setVisible]         = useState(false)   // para animación
  const [showPrefs, setShowPrefs]     = useState(false)
  const [prefs, setPrefs]             = useState({ analytics: false, marketing: false })
  const dialogRef = useRef(null)

  const openBanner = (withPrefs = false) => {
    const saved = readConsent()
    if (saved) setPrefs({ analytics: saved.analytics, marketing: saved.marketing })
    setShowPrefs(withPrefs)
    setOpen(true)
    requestAnimationFrame(() => setVisible(true))
  }

  const close = () => {
    setVisible(false)
    setTimeout(() => setOpen(false), 250)
  }

  // Mostrar al cargar si no hay consentimiento guardado
  useEffect(() => {
    const saved = readConsent()
    if (saved) {
      window.rcsCookieConsent = saved
      window.dispatchEvent(new CustomEvent("rcs:cookie-consent", { detail: saved }))
    } else {
      const t = setTimeout(() => openBanner(false), SHOW_DELAY_MS)
      return () => clearTimeout(t)
    }
  }, [])

  // API global para reabrir (p.ej. link "Cookie Settings" en el footer)
  useEffect(() => {
    window.rcsOpenCookieSettings = () => openBanner(true)
    const onClick = (e) => {
      const trigger = e.target.closest?.("[data-cookie-settings]")
      if (trigger) {
        e.preventDefault()
        openBanner(true)
      }
    }
    document.addEventListener("click", onClick)
    return () => {
      document.removeEventListener("click", onClick)
      delete window.rcsOpenCookieSettings
    }
  }, [])

  // Escape cierra solo si ya hay consentimiento (si no, debe elegir)
  useEffect(() => {
    if (!open) return
    const onKey = (e) => {
      if (e.key === "Escape" && readConsent()) close()
    }
    document.addEventListener("keydown", onKey)
    dialogRef.current?.focus()
    return () => document.removeEventListener("keydown", onKey)
  }, [open])

  const acceptAll = () => { saveConsent({ analytics: true, marketing: true }); close() }
  const rejectAll = () => { saveConsent({ analytics: false, marketing: false }); close() }
  const savePrefs = () => { saveConsent(prefs); close() }

  if (!open) return null

  const hasConsent = !!readConsent()

  const btnBase = {
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: ".08em",
    textTransform: "uppercase",
    borderRadius: 999,
    padding: "11px 18px",
    transition: "background .15s, color .15s, border-color .15s",
    cursor: "pointer",
  }

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-end justify-center sm:justify-start p-3 sm:p-5"
      style={{
        background: showPrefs ? "rgba(0,0,0,0.45)" : "transparent",
        pointerEvents: showPrefs ? "auto" : "none",
        transition: "background .25s",
      }}
      onClick={(e) => { if (showPrefs && hasConsent && e.target === e.currentTarget) close() }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal={showPrefs ? "true" : "false"}
        aria-labelledby="rcs-cookie-title"
        className="w-full outline-none"
        style={{
          maxWidth: showPrefs ? "30rem" : "26rem",
          background: BG,
          border: `1px solid ${BORDER}`,
          borderRadius: 24,
          boxShadow: "0 22px 60px rgba(0,0,0,0.18)",
          overflow: "hidden",
          pointerEvents: "auto",
          fontFamily: "'DM Sans', sans-serif",
          color: INK,
          transform: visible ? "translateY(0)" : "translateY(24px)",
          opacity: visible ? 1 : 0,
          transition: "transform .3s ease, opacity .3s ease, max-width .25s ease",
        }}
      >
        {/* ── Header ── */}
        <div
          className="relative overflow-hidden px-5 py-4 text-white"
          style={{ background: `linear-gradient(135deg, ${INK} 0%, #3a0000 60%, ${RED} 130%)` }}
        >
          <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
            backgroundImage: "linear-gradient(45deg, rgba(255,255,255,.5) 25%, transparent 25%, transparent 50%, rgba(255,255,255,.5) 50%, rgba(255,255,255,.5) 75%, transparent 75%)",
            backgroundSize: "16px 16px",
          }} />
          <div className="relative flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center rounded-full"
                style={{ width: 36, height: 36, background: "rgba(255,255,255,0.12)" }}>
                <CookieIcon className="w-5 h-5" />
              </span>
              <h2 id="rcs-cookie-title"
                style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontSize: 15, letterSpacing: ".02em" }}>
                {showPrefs ? "Cookie Preferences" : "We Value Your Privacy"}
              </h2>
            </div>
            {hasConsent && (
              <button type="button" onClick={close} aria-label="Close"
                className="rounded-full p-1.5 hover:bg-white/10 transition-colors">
                <CloseIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* ── Body ── */}
        <div className="px-5 pt-4 pb-5">
          <p style={{ fontSize: 14, lineHeight: 1.6, color: MUTED }}>
            We use cookies to keep the site running, understand how it's used, and improve your
            experience. You can accept all, reject non-essential cookies, or choose your preferences.{" "}
            <a href={PRIVACY_LINK} style={{ color: RED, fontWeight: 600, textDecoration: "underline" }}>
              Privacy Policy
            </a>
          </p>

          {showPrefs && (
            <ul className="mt-4 flex flex-col gap-2.5" style={{ maxHeight: "45vh", overflowY: "auto" }}>
              {CATEGORIES.map((c) => {
                const checked = c.locked ? true : prefs[c.key]
                return (
                  <li key={c.key} className="flex items-start justify-between gap-4 rounded-2xl px-4 py-3"
                    style={{ background: WHITE, border: `1px solid ${BORDER}` }}>
                    <div>
                      <p style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontSize: 13 }}>
                        {c.title}
                      </p>
                      <p style={{ fontSize: 12.5, lineHeight: 1.5, color: MUTED, marginTop: 2 }}>{c.desc}</p>
                    </div>
                    <Toggle
                      label={c.title}
                      checked={checked}
                      disabled={c.locked}
                      onChange={(v) => setPrefs((p) => ({ ...p, [c.key]: v }))}
                    />
                  </li>
                )
              })}
            </ul>
          )}

          {/* ── Actions ── */}
          <div className="mt-5 flex flex-col sm:flex-row gap-2">
            {showPrefs ? (
              <>
                <button type="button" onClick={savePrefs} className="flex-1"
                  style={{ ...btnBase, background: WHITE, color: INK, border: `1.5px solid ${INK}` }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = INK; e.currentTarget.style.color = WHITE }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = WHITE; e.currentTarget.style.color = INK }}>
                  Save Choices
                </button>
                <button type="button" onClick={acceptAll} className="flex-1"
                  style={{ ...btnBase, background: RED, color: WHITE, border: `1.5px solid ${RED}` }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = RED_DARK }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = RED }}>
                  Accept All
                </button>
              </>
            ) : (
              <>
                <button type="button" onClick={rejectAll} className="flex-1"
                  style={{ ...btnBase, background: WHITE, color: INK, border: `1.5px solid ${BORDER}` }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = INK }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = BORDER }}>
                  Reject
                </button>
                <button type="button" onClick={acceptAll} className="flex-1"
                  style={{ ...btnBase, background: RED, color: WHITE, border: `1.5px solid ${RED}` }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = RED_DARK }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = RED }}>
                  Accept All
                </button>
              </>
            )}
          </div>

          {!showPrefs && (
            <button type="button" onClick={() => setShowPrefs(true)}
              className="mt-3 w-full text-center"
              style={{ fontSize: 12.5, fontWeight: 600, color: MUTED, textDecoration: "underline", cursor: "pointer" }}>
              Customize preferences
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
